import { logger } from '../../common/logger';
import { Focus, Transitions } from './typings';
import { resolve } from './resolve';
import { fromCoords } from './utils';
import { spreadOffsets } from '../keyboard/squareKeys';
import { isKeyboardKeys } from '../typeGuards';

/**
 * Default transitions implementation, returns the next "natural" container in
 * all directions. Ie. given a container with coordinates [x, y] (row, column);
 *
 *                     [x - 1, y]
 *                         ⬆
 *                       ABOVE
 *                         ⬆
 * [x, y - 1] ⬅ PREV ⬅  [x, y] ➡ NEXT ➡ [x, y + 1]
 *                         ⬇
 *                        BELOW
 *                         ⬇
 *                     [x + 1, y]
 *
 */
export const defaultTransitions: Transitions = {
  NEXT: args => {
    const containerToEnter = args.getContainer(
      args.currentFocus.row,
      args.currentFocus.column + 1
    );

    return containerToEnter
      ? resolve({
          ...args,
          currentContainer: containerToEnter,
          entryDirection: 'NEXT',
        })
      : (logger.debug('no NEXT container'), args.currentFocus);
  },
  PREV: args => {
    const containerToEnter = args.getContainer(
      args.currentFocus.row,
      args.currentFocus.column - 1
    );

    return containerToEnter
      ? resolve({
          ...args,
          currentContainer: containerToEnter,
          entryDirection: 'PREV',
        })
      : (logger.debug('no PREV container'), args.currentFocus);
  },
  ABOVE: args => {
    const containerToEnter = args.getContainer(
      args.currentFocus.row - 1,
      args.currentFocus.column
    );

    return containerToEnter
      ? resolve({
          ...args,
          currentContainer: containerToEnter,
          entryDirection: 'ABOVE',
        })
      : (logger.debug('no ABOVE container'), args.currentFocus);
  },
  BELOW: args => {
    const containerToEnter = args.getContainer(
      args.currentFocus.row + 1,
      args.currentFocus.column
    );

    return containerToEnter
      ? resolve({
          ...args,
          currentContainer: containerToEnter,
          entryDirection: 'BELOW',
        })
      : (logger.debug('no BELOW container'), args.currentFocus);
  },
};

/**
 * Modifies the default transitions by blocking exiting to the right. Supports
 * stripes and grids.
 *
 * Actual implementations probably wants to open the sidebar to the left.
 */
export const viewTransitions: Transitions = {
  ...defaultTransitions,
  ABOVE: args => {
    const containerToEnter = args.getContainer(
      args.currentFocus.row - 1,
      args.currentFocus.column
    );

    if (containerToEnter?.constraint.width) {
      const rowsInAboveContainer = Math.floor(
        containerToEnter.content.length / containerToEnter.constraint.width
      );

      return fromCoords({
        ...args.currentFocus,
        row: args.currentFocus.row - 1,
        offset: Math.min(
          rowsInAboveContainer * containerToEnter.constraint.width,
          containerToEnter.content.length - 1
        ),
      });
    }

    return defaultTransitions.ABOVE(args);
  },
  NEXT: args => {
    if (args.currentContainer?.constraint.width) {
      // grids wrap around to the next row when reaching the right edge
      const newFocus = fromCoords({
        ...args.currentFocus,
        offset: args.currentFocus.offset + 1,
      });

      if (newFocus.offset > args.currentContainer.content.length - 1) {
        return args.currentFocus;
      }

      return newFocus;
    }
    return args.currentFocus;
  },
};

/**
 * Modifies the default transitions by blocking exiting to the left and right.
 */
export const noHorizontalTransitions: Transitions = {
  ...defaultTransitions,
  NEXT: args => args.currentFocus,
  PREV: args => args.currentFocus,
};

/**
 * Modifies the default transitions by blocking exiting above and below the containers
 */
export const verticalColumnsTransitions: Transitions = {
  ...defaultTransitions,
  ABOVE: args => args.currentFocus,
  BELOW: args => args.currentFocus,
};

/**
 * Returns the current focus in all directions
 */
export const singleContainerTransitions: Transitions = {
  PREV: args => args.currentFocus,
  NEXT: args => args.currentFocus,
  ABOVE: args => args.currentFocus,
  BELOW: args => args.currentFocus,
};

/**
 * Handles keyboard-related transitions. Defaults to defaultTransitions
 * @param getPreviousNav Needed to handle transitions between different sized rows more naturally
 * @param keyboardRowIndexFirst
 * @param keyboardRowIndexLast
 */
export function getKeyboardContainerTransitions(
  getPreviousNav: () => { from: Focus | null; to: Focus | null },
  keyboardRowIndexFirst = 0,
  keyboardRowIndexLast = keyboardRowIndexFirst + 2
): Transitions {
  return {
    NEXT: args => {
      const newFocus = defaultTransitions.NEXT(args);

      const prevNav = getPreviousNav();

      prevNav.from = args.currentFocus;
      prevNav.to = newFocus;

      return newFocus;
    },
    PREV: args => {
      const prevNav = getPreviousNav();

      if (prevNav.from !== null && prevNav.to?.id === args.currentFocus.id) {
        return prevNav.from;
      }

      return defaultTransitions.PREV(args);
    },
    ABOVE: args => {
      const { currentFocus, getContainer, currentContainer } = args;

      const rowNew = currentFocus.row - 1;
      const containerAbove = getContainer(rowNew, currentFocus.column);

      const contentCurrent = currentContainer?.content;
      const contentAbove = getContainer(rowNew, currentFocus.column)?.content;

      if (
        !containerAbove ||
        !contentAbove ||
        !isKeyboardKeys(contentAbove) ||
        !currentContainer ||
        !contentCurrent ||
        !isKeyboardKeys(contentCurrent)
      ) {
        logger.warn('transitions received non-keyboard-key navigation trees');
        return currentFocus;
      }

      const prevNav = getPreviousNav();
      const focusRestored = prevNav.from;

      prevNav.from = currentFocus;

      if (focusRestored !== null && prevNav.to?.id === currentFocus.id) {
        prevNav.to = focusRestored;

        return focusRestored;
      }

      if (rowNew < keyboardRowIndexFirst) {
        const focusNew = defaultTransitions.ABOVE(args);
        prevNav.to = focusNew;

        return focusNew;
      }

      const emptyKeyCountCurrent = contentCurrent.reduce(
        (count: number, key) => count + Number(key.value === '<empty>'),
        0
      );

      let focusNew;

      if (emptyKeyCountCurrent > 0) {
        const rowsCountAbove = containerAbove.constraint.width
          ? Math.floor(
              containerAbove.content.length / containerAbove.constraint.width
            )
          : 1;
        const lastRowOffsetAbove =
          (rowsCountAbove - 1) * (containerAbove.constraint.width ?? 0);

        focusNew = fromCoords({
          ...currentFocus,
          row: rowNew,
          offset:
            currentFocus.offset + emptyKeyCountCurrent + lastRowOffsetAbove,
        });
      } else {
        const rowOffsetsCurrent = spreadOffsets(contentCurrent);
        const rowOffsetsAbove = spreadOffsets(contentAbove).splice(
          -(containerAbove.constraint.width ?? 0) // takes last row of the above container
        );

        const pivotOffset =
          currentContainer.content.length < containerAbove.content.length
            ? rowOffsetsCurrent.findIndex(e => e === currentFocus.offset)
            : currentFocus.offset;

        focusNew = fromCoords({
          ...currentFocus,
          row: rowNew,
          offset: rowOffsetsAbove[pivotOffset] ?? 0,
        });
      }

      prevNav.to = focusNew;

      return focusNew;
    },
    BELOW: args => {
      const { currentFocus, getContainer, currentContainer } = args;

      const prevNav = getPreviousNav();
      const focusRestored = prevNav.from;

      prevNav.from = currentFocus;

      if (focusRestored !== null && prevNav.to?.id === currentFocus.id) {
        prevNav.to = focusRestored;

        return focusRestored;
      }

      if (currentFocus.row > keyboardRowIndexLast + 2) {
        const focusNew = defaultTransitions.BELOW(args);
        prevNav.to = focusNew;

        return focusNew;
      }

      const rowNew = currentFocus.row + 1;
      let focusNew: Focus;

      const containerBelow = getContainer(rowNew, currentFocus.column);
      const contentBelow = containerBelow?.content;
      const contentCurrent = currentContainer?.content;

      if (
        !containerBelow ||
        !contentBelow ||
        !isKeyboardKeys(contentBelow) ||
        !currentContainer ||
        !contentCurrent ||
        !isKeyboardKeys(contentCurrent)
      ) {
        logger.warn('transitions received non-keyboard-key navigation trees');
        return currentFocus;
      }

      const emptyKeyCountBelow = contentBelow.reduce(
        (count, key) => count + Number(key.value === '<empty>'),
        0
      );

      if (emptyKeyCountBelow > 0) {
        // assuming bottom row has only the alphabetical-toggle-button and the enter-button
        const shouldFocusToggleButton =
          currentFocus.offset % (currentContainer.constraint.width || 1) <=
          emptyKeyCountBelow;

        focusNew = fromCoords({
          ...defaultTransitions.BELOW(args),
          offset: shouldFocusToggleButton ? 0 : 1,
        });
      } else {
        const rowOffsetsCurrent = spreadOffsets(contentCurrent);
        const rowOffsetsBelow = spreadOffsets(contentBelow);

        const pivotOffset =
          contentCurrent.length < contentBelow.length
            ? rowOffsetsCurrent.findIndex(e => e === currentFocus.offset)
            : currentFocus.offset % (currentContainer.constraint.width || 1);

        focusNew = fromCoords({
          ...currentFocus,
          row: rowNew,
          offset: rowOffsetsBelow[pivotOffset] || 0,
        });
      }

      prevNav.to = focusNew;

      return focusNew;
    },
  };
}
